import React, { useEffect, useId, useRef, useState } from 'react';
import { updateClient } from './api';
import { useTranslation } from '../i18n';

function getNextVisitDate(frequency, interval, dayOfWeek, dayOfMonth) {
  const today = new Date();
  const next = new Date(today);

  if (frequency === 'weekly' || frequency === 'biweekly' || frequency === 'custom_weeks') {
    const weeks = frequency === 'weekly' ? 1 : frequency === 'biweekly' ? 2 : interval;
    if (dayOfWeek !== null && dayOfWeek !== undefined) {
      const daysUntil = (dayOfWeek - today.getDay() + 7) % 7 || 7;
      next.setDate(today.getDate() + daysUntil);
    } else {
      next.setDate(today.getDate() + weeks * 7);
    }
  } else if (frequency === 'monthly') {
    next.setMonth(today.getMonth() + 1);
    if (dayOfMonth) next.setDate(dayOfMonth);
  } else if (frequency === 'one_time') {
    return null;
  }

  return next.toISOString().split('T')[0];
}

export default function RecurringModal({ client, onClose, onUpdated }) {
  const { t } = useTranslation();
  const titleId = useId();
  const visitTypeGroupId = useId();
  const visitDateId = useId();
  const frequencyId = useId();
  const intervalId = useId();
  const dowId = useId();
  const domId = useId();
  const nextVisitId = useId();
  const nextVisitHintId = useId();
  const amountId = useId();
  const notesId = useId();
  const errorId = useId();

  const [form, setForm] = useState({
    visit_type: client.visit_type || 'recurring',
    recurring_enabled: client.recurring_enabled || false,
    recurring_frequency: client.recurring_frequency || 'weekly',
    recurring_interval: client.recurring_interval || 1,
    recurring_day_of_week: client.recurring_day_of_week ?? '',
    recurring_day_of_month: client.recurring_day_of_month || '',
    recurring_amount: client.recurring_amount || '',
    recurring_notes: client.recurring_notes || '',
    next_visit_date: client.next_visit_date || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const firstFocusRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    if (firstFocusRef.current) firstFocusRef.current.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const days = [
    t('recur.day_sunday'),
    t('recur.day_monday'),
    t('recur.day_tuesday'),
    t('recur.day_wednesday'),
    t('recur.day_thursday'),
    t('recur.day_friday'),
    t('recur.day_saturday')
  ];

  const visitTypes = [
    { value: 'recurring', label: t('recur.type_recurring'), emoji: '🔄' },
    { value: 'one_time', label: t('recur.type_one_time'), emoji: '1️⃣' },
    { value: 'none', label: t('recur.type_none'), emoji: '⛔' }
  ];

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const nextDate = form.visit_type === 'one_time'
        ? form.next_visit_date
        : getNextVisitDate(
            form.recurring_frequency,
            parseInt(form.recurring_interval),
            form.recurring_day_of_week !== '' ? parseInt(form.recurring_day_of_week) : null,
            form.recurring_day_of_month ? parseInt(form.recurring_day_of_month) : null
          );

      const { data } = await updateClient(client.id, {
        visit_type: form.visit_type,
        recurring_enabled: form.visit_type === 'recurring',
        recurring_frequency: form.visit_type === 'recurring' ? form.recurring_frequency : null,
        recurring_interval: parseInt(form.recurring_interval) || 1,
        recurring_day_of_week: form.recurring_day_of_week !== '' ? parseInt(form.recurring_day_of_week) : null,
        recurring_day_of_month: form.recurring_day_of_month ? parseInt(form.recurring_day_of_month) : null,
        recurring_amount: parseFloat(form.recurring_amount) || 0,
        recurring_notes: form.recurring_notes,
        next_visit_date: nextDate
      });
      setSaving(false);
      onUpdated(data);
      onClose();
    } catch {
      setError(t('recur.error_generic'));
      setSaving(false);
    }
  };

  const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div style={styles.overlay} onClick={onOverlayClick}>
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={error ? errorId : undefined}
      >
        <div style={styles.header}>
          <div>
            <h2 id={titleId} style={styles.title}>{t('recur.title')}</h2>
            <div style={styles.sub}>{client.name}</div>
          </div>
          <button
            type="button"
            style={styles.closeBtn}
            onClick={onClose}
            aria-label={t('a11y.close_dialog')}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        {error && (
          <div id={errorId} role="alert" style={styles.errorBox}>
            {error}
          </div>
        )}

        <fieldset style={styles.field}>
          <legend id={visitTypeGroupId} style={styles.label}>{t('recur.visit_type')}</legend>
          <div style={styles.typeRow} role="radiogroup" aria-labelledby={visitTypeGroupId}>
            {visitTypes.map((opt, idx) => {
              const checked = form.visit_type === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  ref={idx === 0 ? firstFocusRef : undefined}
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked || (!visitTypes.some(o => o.value === form.visit_type) && idx === 0) ? 0 : -1}
                  style={{
                    ...styles.typeBtn,
                    ...(checked ? styles.typeBtnActive : {})
                  }}
                  onClick={() => setForm({ ...form, visit_type: opt.value })}
                >
                  <span aria-hidden="true">{opt.emoji}</span> {opt.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        {form.visit_type === 'one_time' && (
          <div style={styles.field}>
            <label htmlFor={visitDateId} style={styles.label}>{t('recur.visit_date')}</label>
            <input
              id={visitDateId}
              type="date"
              style={styles.input}
              value={form.next_visit_date}
              onChange={e => setForm({ ...form, next_visit_date: e.target.value })}
              aria-required="true"
            />
          </div>
        )}

        {form.visit_type === 'recurring' && (
          <>
            <div style={styles.field}>
              <label htmlFor={frequencyId} style={styles.label}>{t('recur.frequency')}</label>
              <select
                id={frequencyId}
                style={styles.input}
                value={form.recurring_frequency}
                onChange={e => setForm({ ...form, recurring_frequency: e.target.value })}
              >
                <option value="weekly">{t('recur.freq_weekly')}</option>
                <option value="biweekly">{t('recur.freq_biweekly')}</option>
                <option value="custom_weeks">{t('recur.freq_custom')}</option>
                <option value="monthly">{t('recur.freq_monthly')}</option>
              </select>
            </div>

            {form.recurring_frequency === 'custom_weeks' && (
              <div style={styles.field}>
                <label htmlFor={intervalId} style={styles.label}>{t('recur.every_how_many_weeks')}</label>
                <input
                  id={intervalId}
                  type="number"
                  inputMode="numeric"
                  style={styles.input}
                  min="1"
                  max="12"
                  value={form.recurring_interval}
                  onChange={e => setForm({ ...form, recurring_interval: e.target.value })}
                />
              </div>
            )}

            {(form.recurring_frequency === 'weekly' ||
              form.recurring_frequency === 'biweekly' ||
              form.recurring_frequency === 'custom_weeks') && (
              <div style={styles.field}>
                <label htmlFor={dowId} style={styles.label}>{t('recur.preferred_day_optional')}</label>
                <select
                  id={dowId}
                  style={styles.input}
                  value={form.recurring_day_of_week}
                  onChange={e => setForm({ ...form, recurring_day_of_week: e.target.value })}
                >
                  <option value="">{t('recur.any_day')}</option>
                  {days.map((d, i) => (
                    <option key={i} value={i}>{d}</option>
                  ))}
                </select>
              </div>
            )}

            {form.recurring_frequency === 'monthly' && (
              <div style={styles.field}>
                <label htmlFor={domId} style={styles.label}>{t('recur.day_of_month')}</label>
                <input
                  id={domId}
                  type="number"
                  inputMode="numeric"
                  style={styles.input}
                  min="1"
                  max="31"
                  placeholder={t('recur.day_of_month_placeholder')}
                  value={form.recurring_day_of_month}
                  onChange={e => setForm({ ...form, recurring_day_of_month: e.target.value })}
                />
              </div>
            )}

            <div style={styles.field}>
              <label htmlFor={nextVisitId} style={styles.label}>{t('recur.next_visit_date')}</label>
              <input
                id={nextVisitId}
                type="date"
                style={styles.input}
                value={form.next_visit_date}
                onChange={e => setForm({ ...form, next_visit_date: e.target.value })}
                aria-describedby={nextVisitHintId}
              />
              <div id={nextVisitHintId} style={styles.hint}>{t('recur.next_visit_hint')}</div>
            </div>
          </>
        )}

        <div style={styles.field}>
          <label htmlFor={amountId} style={styles.label}>{t('recur.amount_each_visit')}</label>
          <input
            id={amountId}
            type="number"
            inputMode="decimal"
            style={styles.input}
            placeholder="0.00"
            value={form.recurring_amount}
            onChange={e => setForm({ ...form, recurring_amount: e.target.value })}
          />
        </div>

        <div style={styles.field}>
          <label htmlFor={notesId} style={styles.label}>{t('recur.schedule_notes')}</label>
          <input
            id={notesId}
            type="text"
            style={styles.input}
            placeholder={t('recur.schedule_notes_placeholder')}
            value={form.recurring_notes}
            onChange={e => setForm({ ...form, recurring_notes: e.target.value })}
          />
        </div>

        <div style={styles.actions}>
          <button type="button" style={styles.cancelBtn} onClick={onClose}>
            {t('recur.cancel')}
          </button>
          <button
            type="button"
            style={{ ...styles.saveBtn, ...(saving ? styles.saveBtnDisabled : {}) }}
            onClick={handleSave}
            disabled={saving}
            aria-busy={saving}
          >
            {saving ? t('recur.saving') : t('recur.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 400, margin: '0 16px', maxHeight: '90vh', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 15, fontWeight: 500, color: '#1a1a1a', margin: 0 },
  sub: { fontSize: 12, color: '#5f6b58', marginTop: 2 },
  closeBtn: { border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#5f6b58', padding: 8, minWidth: 32, minHeight: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  errorBox: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '8px 10px', fontSize: 13, marginBottom: 12 },
  field: { marginBottom: 14, border: 'none', padding: 0 },
  label: { display: 'block', fontSize: 12, fontWeight: 500, color: '#5f6b58', marginBottom: 5, padding: 0 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, color: '#1a1a1a', boxSizing: 'border-box' },
  hint: { fontSize: 11, color: '#5f6b58', marginTop: 4 },
  typeRow: { display: 'flex', gap: 6 },
  typeBtn: { flex: 1, padding: '8px 6px', border: '1px solid #ddd', background: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#5f6b58' },
  typeBtnActive: { background: '#EAF3DE', borderColor: '#14653a', color: '#14653a', fontWeight: 500 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
  cancelBtn: { padding: '8px 16px', border: '1px solid #ddd', background: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  saveBtn: { padding: '8px 18px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' },
  saveBtnDisabled: { opacity: 0.7, cursor: 'wait' }
};
